'use client';

import type { ProductVideo, StreamVideo } from '@/domain/products/types';
import { getYouTubeVideoId, isYouTubeUrl, sortVideos } from '@/features/products/utils/videos';
import { toDateString } from '@/utils/datetime';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { Fragment } from 'react';
import YouTube, { type YouTubeProps } from 'react-youtube';

interface VideoSectionProps {
  videos: ProductVideo[];
  streamVideos: StreamVideo[];
}

type Video = ProductVideo | StreamVideo;

const opts: YouTubeProps['opts'] = {
  height: '360',
  width: '640',
};

const sxContainer = { m: 4, maxHeight: '400px', overflowY: 'auto', mr: 0 };
const sxVideoContainer = {
  mb: '2rem',
  iframe: { width: '100%', height: '100%' },
  '@media (min-width:600px)': { iframe: { width: 640, maxWidth: '100%', height: 360 } },
};

const normalizeId = (video: Video) =>
  'idProductVideo' in video ? `video-${video.idProductVideo}` : `stream-${video.idProductStream}`;

const VideoSection = ({ videos, streamVideos }: VideoSectionProps) => (
  <Box sx={sxContainer}>
    {[...videos, ...streamVideos].sort(sortVideos).map((video) => (
      <Fragment key={normalizeId(video)}>
        <Box>
          <Typography variant="cardHeading" component="strong">
            {video.title}
          </Typography>
          <Typography component="p" color="text.secondary" sx={{ mb: 0 }}>
            {video.timestamp && toDateString(video.timestamp, '-')}
          </Typography>
        </Box>
        <Box sx={sxVideoContainer}>
          {isYouTubeUrl(video.hyperlink) ? (
            <YouTube loading="lazy" opts={opts} videoId={getYouTubeVideoId(video.hyperlink)} title={video.title} />
          ) : (
            <iframe src={video.hyperlink} allowFullScreen title={video.title} style={{ border: 'none' }} />
          )}
        </Box>
      </Fragment>
    ))}
  </Box>
);

export default VideoSection;
